import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

export type UsageCircleProps = {
  limit: number | null;
  count: number;
  percentageUsed: number | null;
  percentageUsedColor: string;
  label: string;
  size?: number;
};

const UsageCircle: React.FC<UsageCircleProps> = ({
  limit,
  count,
  percentageUsed,
  percentageUsedColor,
  label,
  size = 120,
}) => {
  const isUnlimited = limit === null;
  const valueTextSize = size * 0.13;
  const labelTextSize = size * 0.09;
  const thickness = 4;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: size,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: size,
          height: size,
        }}
      >
        {/* Background circle */}
        <CircularProgress
          variant='determinate'
          value={100}
          size={size}
          thickness={thickness}
          sx={{
            color: (theme) => theme.palette.grey[200],
            position: 'absolute',
          }}
        />
        {/* Colored progress */}
        <CircularProgress
          variant='determinate'
          value={isUnlimited ? 100 : percentageUsed || 0}
          size={size}
          thickness={thickness}
          sx={{
            color: percentageUsedColor,
            position: 'absolute',
          }}
        />

        {/* Center content */}
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            width: size * 0.8,
            height: size * 0.8,
          }}
        >
          {isUnlimited ? (
            <AllInclusiveIcon
              sx={{
                fontSize: size * 0.22,
                color: percentageUsedColor,
                mb: 0.5,
              }}
            />
          ) : (
            <Typography
              variant='body1'
              component='div'
              sx={{
                fontSize: valueTextSize,
                fontWeight: 'medium',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              {`${count} / ${limit}`}
            </Typography>
          )}

          <Typography
            variant='body2'
            sx={{
              fontSize: labelTextSize,
              lineHeight: 1.2,
              mt: 0.5,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default UsageCircle;
